import { HttpClient, HttpParams } from '@angular/common/http';
import { compact } from 'lodash';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AlertViewModelFactory } from '../alerts/view-models/alert-view-model-factory.service';
import { httpOptionsWithSkipErrorInterceptor } from '../common/functions-and-constant/interceptor';
import { Alert, AlertEntity, AlertStatus } from '../models/alert';
import { AlertViewModel } from '../alerts/view-models/alert.view-model';
import { URLService } from './dynamic-url.service';
import { NotificationToasterService } from './notification-toaster.service';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  public selectedAlert$ = new BehaviorSubject<AlertViewModel<AlertEntity> | null>(null);
  public readonly refresh$ = new BehaviorSubject<void>(undefined);

  constructor(
    private readonly http: HttpClient,
    private readonly urlService: URLService,
    private readonly alertViewModelFactory: AlertViewModelFactory,
    private readonly notificationToasterService: NotificationToasterService,
    private readonly translateService: TranslateService,
  ) {}

  setSelectedAlert(alert: AlertViewModel<AlertEntity> | null): void {
    this.selectedAlert$.next(alert);
  }

  refresh(): void {
    this.refresh$.next();
  }

  /**
   * GET /alerts — institution is inferred from the authenticated user.
   * @param statuses Optional filter; repeated `status` query params (array).
   */
  getAlerts(statuses?: AlertStatus[]): Observable<AlertViewModel<AlertEntity>[]> {
    let params = new HttpParams();
    if (statuses?.length) {
      params = params.appendAll({ 'status[]': statuses });
    }

    return this.http
      .get<{ alerts: Alert<AlertEntity>[] }>(`${this.urlService.getDynamicUrl()}/alerts`, {
        params,
        ...httpOptionsWithSkipErrorInterceptor,
      })
      .pipe(
        map((response) =>
          compact(response.alerts.map((alert) => this.alertViewModelFactory.get(alert))),
        ),
      );
  }

  updateAlert(id: string, payload: Partial<Alert<AlertEntity>>): Observable<Alert<AlertEntity>> {
    return this.http
      .patch<{ alert: Alert<AlertEntity> }>(
        `${this.urlService.getDynamicUrl()}/alerts/${id}`,
        payload,
        {
          ...httpOptionsWithSkipErrorInterceptor,
        },
      )
      .pipe(map((response) => response.alert));
  }

  /**
   * Patches the alert, updates the selected list row, and triggers a list refresh.
   * @param onSuccess Optional hook after sync (e.g. success toast, close panel).
   */
  updateAlertAndSyncState(
    alertId: string,
    alertUpdate: Partial<Alert<AlertEntity>>,
    onSuccess?: () => void,
  ): void {
    this.updateAlert(alertId, alertUpdate).subscribe({
      next: (updatedAlert: Alert<AlertEntity>) => {
        // PATCH populates top-level refs but not the entity's nested ones, and
        // cannot change the entity anyway — so keep the one already in hand.
        // Non-null: this only runs for the alert open in the details panel.
        const entity = this.selectedAlert$.value!.entity;
        this.setSelectedAlert(this.alertViewModelFactory.get({ ...updatedAlert, entity }));
        this.refresh();
        onSuccess?.();
      },
      error: () => {
        this.notificationToasterService.showDefaultErrorNotificationV2(
          this.translateService.instant('Failed to update alert'),
          '',
          this.notificationToasterService.generateNotificationCode(),
        );
      },
    });
  }
}
